/**
 * LayoutBar — the floor plan selector, built to match the Multi-Site View toolbar so the
 * two "pick a saved layout" surfaces behave identically: an icon-only "New layout" button
 * beside a dropdown whose rows carry the name, a default star, a one-line summary, and
 * per-row edit / set-default / delete actions. Both triggers are symbols with tooltips —
 * the active layout's name belongs to the title bar, which shows it in full.
 *
 * The one addition over Multi-Site is a live alert count per row — an operator watching
 * Floor 2 can see that the loading bay has lit up without switching to it first.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  PencilIcon,
  PlusIcon,
  Square3Stack3DIcon,
  StarIcon as StarOutlineIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import Tooltip from '../common/Tooltip';
import type { MapLayoutSummary } from '../../api/contracts';

export interface LayoutBarProps {
  layouts: MapLayoutSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  /** layoutId → number of active alerts on that layout's cameras. */
  alertCounts: Record<string, number>;
  canEdit?: boolean;
  onCreate?: () => void;
  onEdit?: (layout: MapLayoutSummary) => void;
  onSetDefault?: (layout: MapLayoutSummary) => void;
  onDelete?: (layout: MapLayoutSummary) => void;
  /** Set while placements are being edited — switching layout would strand unsaved pins. */
  disabled?: boolean;
}

export default function LayoutBar({
  layouts,
  activeId,
  onSelect,
  alertCounts,
  canEdit,
  onCreate,
  onEdit,
  onSetDefault,
  onDelete,
  disabled,
}: LayoutBarProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on Escape as well as on the backdrop — the dropdown can sit over the canvas,
  // where a stray click is a pan rather than a dismissal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Total alerts NOT on the layout being viewed — surfaced on the closed pill so a
  // single-plan control room still notices another floor going off.
  const elsewhere = layouts.reduce(
    (n, l) => (l.id === activeId ? n : n + (alertCounts[l.id] || 0)),
    0,
  );

  return (
    <div className="flex items-center gap-1.5 sm:gap-2" ref={wrapRef}>
      {/* Icon-only; the label lives in the hover tooltip, as on Multi-Site View. The title
          bar's job is to show the layout NAME in full, and it can only do that if the
          controls beside it stay narrow. */}
      {canEdit && onCreate && (
        <Tooltip label="New layout" side="bottom">
          <button
            type="button"
            onClick={onCreate}
            disabled={disabled}
            aria-label="New layout"
            className="rounded-lg bg-brand p-1.5 text-white shadow-sm ring-1 ring-inset ring-white/10 transition-all hover:bg-brand/90 hover:shadow active:scale-[0.95] disabled:pointer-events-none disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </Tooltip>
      )}

      <div className="relative">
        <Tooltip
          label={
            elsewhere > 0
              ? `Layouts — ${elsewhere} active alert${elsewhere === 1 ? '' : 's'} on other layouts`
              : 'Layouts'
          }
          side="bottom"
        >
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Layouts"
            className="flex items-center gap-1 rounded-lg bg-muted p-1.5 text-foreground transition-colors hover:bg-accent"
          >
            <Square3Stack3DIcon className="h-4 w-4" />
            {/* The active layout's name is shown large in the centre of the title bar, so
                this trigger only names the menu. The badge stays: it counts alerts on the
                layouts you AREN'T looking at, which nothing else on screen reports. */}
            {elsewhere > 0 && (
              <span className="min-w-[16px] animate-pulse rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-4 text-white tabular-nums">
                {elsewhere}
              </span>
            )}
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </Tooltip>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-xl">
              {layouts.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-muted-foreground">No saved layouts</p>
              ) : (
                <div className="max-h-60 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                  {layouts.map((l) => {
                    const alerts = alertCounts[l.id] || 0;
                    return (
                      <div
                        key={l.id}
                        /* The current layout is marked in INK, not brand. Selection on this
                           board is neutral throughout now (see SegmentedToggle and the filter
                           chips): `--brand` is a vermilion close enough to `--destructive` that
                           on a page about critical alerts it kept reading as one. */
                        className={`flex items-center gap-2 px-3 py-2 transition-colors hover:bg-accent/50 ${
                          l.id === activeId ? 'bg-muted' : ''
                        }`}
                      >
                        <button
                          type="button"
                          disabled={disabled && l.id !== activeId}
                          title={
                            disabled && l.id !== activeId
                              ? 'Save or cancel your changes before switching layout'
                              : l.name
                          }
                          onClick={() => {
                            onSelect(l.id);
                            setOpen(false);
                          }}
                          className="group/name min-w-0 flex-1 text-left disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <div className="flex items-center gap-1.5">
                            <p className="truncate text-sm font-medium text-foreground transition-colors group-hover/name:text-brand">
                              {l.name}
                            </p>
                            {l.is_default && <StarIcon className="h-3 w-3 flex-shrink-0 text-amber-500" />}
                            {alerts > 0 && (
                              <span className="min-w-[16px] flex-shrink-0 animate-pulse rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-4 text-white tabular-nums">
                                {alerts}
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground">
                            {l.camera_count} camera{l.camera_count === 1 ? '' : 's'}
                            {l.description ? ` · ${l.description}` : ''}
                          </p>
                        </button>

                        {canEdit && onEdit && (
                          <button
                            type="button"
                            title="Edit layout"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              onEdit(l);
                            }}
                            className="flex-shrink-0 p-1 text-muted-foreground transition-colors hover:text-brand"
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                        )}

                        {canEdit && onSetDefault && (
                          <button
                            type="button"
                            title={l.is_default ? 'Opens by default' : 'Set as default'}
                            disabled={l.is_default}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSetDefault(l);
                            }}
                            className="flex-shrink-0 p-1 text-muted-foreground transition-colors hover:text-amber-500 disabled:cursor-default"
                          >
                            {l.is_default ? (
                              <StarIcon className="h-3.5 w-3.5 text-amber-500" />
                            ) : (
                              <StarOutlineIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}

                        {canEdit && onDelete && (
                          <button
                            type="button"
                            title="Delete layout"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              onDelete(l);
                            }}
                            className="flex-shrink-0 p-1 text-muted-foreground transition-colors hover:text-red-400"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
